/** @jsx jsx */
import React, {
	Component,
	type ComponentType,
	type ReactNode,
	// eslint-disable-next-line jira/restricted/react-component-props
	type ComponentProps,
	type SyntheticEvent,
} from 'react';
import { css, styled, jsx } from '@compiled/react';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import qs from 'query-string';
// @ts-expect-error - TS2305 - Module '"react-router-dom"' has no exported member 'ContextRouter'.
import type { ContextRouter } from 'react-router-dom';
import {
	type UIAnalyticsEvent,
	type CreateUIAnalyticsEvent,
	AnalyticsContext,
} from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import DropdownMenu, {
	DropdownItemCheckboxGroup,
	DropdownItemCheckbox,
} from '@atlaskit/dropdown-menu';
import Heading from '@atlaskit/heading';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import SearchClose from '@atlaskit/icon/glyph/editor/close';
import SearchIcon from '@atlaskit/icon/glyph/editor/search';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import Textfield from '@atlaskit/textfield';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import ViewTracker from '@atlassian/jira-analytics-web-react/src/components/view-tracker.tsx';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import OpTrigger from '@atlassian/jira-common-util-op-triggers/src/on-mount/OpTrigger.tsx';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { fg } from '@atlassian/jira-feature-gating';
import { type IntlShape, FormattedMessage, injectIntl } from '@atlassian/jira-intl';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import ExternalLinkButton from '@atlassian/jira-packages-components-external-link-button/src/view.tsx';
import { SCREEN } from '@atlassian/jira-product-analytics-bridge';
import searchNoResultsImage from '@atlassian/jira-servicedesk-common/src/utils/assets/no-search-results.svg';
import type { ConfluenceSpace } from '@atlassian/jira-servicedesk-knowledge-common/src/common/types.tsx';
import { KbContentContainer } from '@atlassian/jira-servicedesk-knowledge-common/src/common/ui/content-container/index.tsx';
import { KnowledgeBaseActionsGroup } from '@atlassian/jira-servicedesk-knowledge-common/src/common/ui/kb-action-group/index.tsx';
import { UserCapabilitiesSubscriber } from '@atlassian/jira-servicedesk-knowledge-common/src/controllers/store/index.tsx';
import { ConfluenceUpsellMessage } from '@atlassian/jira-servicedesk-knowledge-common/src/ui/confluence-upsell-message/index.tsx';
import { CreateArticleButton } from '@atlassian/jira-servicedesk-knowledge-common/src/ui/create-article-button/index.tsx';
import type { OrderBy } from '@atlassian/jira-servicedesk-knowledge-services/src/services/rest/articles/index.tsx';
import type {
	AppLinkId,
	Article,
	GenericError,
	AppLink,
} from '@atlassian/jira-servicedesk-knowledge-services/src/types.tsx';
import type { PushHistory } from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/knowledge-hub-push-provider/index.tsx';
import Redirect from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/redirect/index.tsx';
import { VirtualServiceAgentTouchpoint } from '@atlassian/jira-servicedesk-virtual-agent-knowledge-base-touchpoint/src/ui/index.tsx';
import type { BrowseKnowledgePayload } from '../../state/actions/browse-knowledge/index.tsx';
import type { FetchLinkedSpacesPayload } from '../../state/actions/fetch-linked-spaces/index.tsx';
import type { AppLinkError } from '../../state/entities/applink/types.tsx';
import type { LinkedState } from '../../state/entities/hub/types.tsx';
import AppLinkAuthentication from '../common/applink-authentication/index.tsx';
import EmptyStateImage from '../common/assets/create-articles.svg';
import { DraftArticleSuccessFlags } from '../common/draft-article-flags/view.tsx';
import ErrorPage from '../common/error-page/index.tsx';
import commonMessages from '../common/i18n/messages.tsx';
import PageHeader from '../common/page-header/view.tsx';
import type SearchKnowledgeInline from '../common/search-knowledge-inline/index.tsx';
import messages from './messages.tsx';
import { articlesPageLoad } from './utils/performance-analytics/index.tsx';

export type Page = {
	totalSize: number;
	articles: Article[];
};

export type KnowledgeHubBrowseArticlesProps = {
	isLoading: boolean;
	isAdmin: boolean;
	activationId: string;
	SearchKnowledgeInline: ComponentType<
		JSX.LibraryManagedAttributes<
			typeof SearchKnowledgeInline,
			ComponentProps<typeof SearchKnowledgeInline>
		>
	>;
	linkedState: LinkedState;
	confluenceUrl: string | undefined;
	cursor: string;
	nextCursor: string;
	prevCursor: string;
	spaceKey: string | undefined;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	spaceKeys: Record<any, any>;
	searchQuery: string;
	projectId: number;
	page: number;
	sortBy: OrderBy;
	error: GenericError | undefined;
	limit: number;
	articlesPage: Page | undefined;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	ReadyForUser: ComponentType<any>;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	ArticlesTable: ComponentType<any>;
	categoriesSupported: boolean;
	projectKey: string;
	linkedSpaces: {
		name: string;
		key: string;
		appLinkId: AppLinkId;
	}[];
	appLinkOrError: AppLink | AppLinkError | undefined;
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	getLinkedSpaces: (payload: FetchLinkedSpacesPayload) => void;
	onFetchAppLink: () => void;
	onBrowseArticles: (payload: BrowseKnowledgePayload) => void;
	onCreateArticleHeaderClicked: // eslint-disable-next-line @typescript-eslint/no-explicit-any
	((e: SyntheticEvent<any>, analyticsEvent: UIAnalyticsEvent) => void) | undefined;
	onCreateArticleEmptyStateClicked: // eslint-disable-next-line @typescript-eslint/no-explicit-any
	((e: SyntheticEvent<any>, analyticsEvent: UIAnalyticsEvent) => void) | undefined;
};

type InternalProps = KnowledgeHubBrowseArticlesProps & {
	history: ContextRouter['history'];
	push: PushHistory;
	intl: IntlShape;
};

type KnowledgeHubBrowseAllArticlesState = {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	spacesSelected: Record<any, any>;
	query: string;
	dropdownText: string;
	hasLoaded: boolean;
};

// @ts-expect-error - TS7031 - Binding element 'articleUrl' implicitly has an 'any' type. | TS7031 - Binding element 'onCreateArticleHeaderClicked' implicitly has an 'any' type. | TS7031 - Binding element 'intl' implicitly has an 'any' type. | TS7031 - Binding element 'appLinkOrError' implicitly has an 'any' type.
const renderLinkedButton = ({ articleUrl, onCreateArticleHeaderClicked, intl, appLinkOrError }) => {
	const isConfluenceServerAppLink = Boolean(!appLinkOrError?.error && appLinkOrError?.isServer);
	if (!isConfluenceServerAppLink) {
		return (
			<UserCapabilitiesSubscriber>
				{({ data }) => {
					const {
						hasConfluenceLicense,
						spaceUserCapabilities: { editableSpaces },
					} = data;
					return (
						<CreateArticleButton
							spaces={editableSpaces}
							hasConfluenceLicense={hasConfluenceLicense}
						/>
					);
				}}
			</UserCapabilitiesSubscriber>
		);
	}

	if (articleUrl) {
		return (
			<ExternalLinkButton
				href={articleUrl}
				onClick={onCreateArticleHeaderClicked}
				appearance="primary"
				spacing="default"
			>
				{intl.formatMessage(messages.createArticleCta)}
			</ExternalLinkButton>
		);
	}
	return null;
};
// eslint-disable-next-line jira/react/no-class-components
export class KnowledgeHubBrowseAllArticles extends Component<
	InternalProps,
	KnowledgeHubBrowseAllArticlesState
> {
	// go/jfe-eslint

	static defaultProps = {
		page: 1,
		error: undefined,
		categoriesSupported: true,
		onCreateArticleHeaderClicked: undefined,
		onCreateArticleEmptyStateClicked: undefined,
	};

	static getDerivedStateFromProps(props: InternalProps, state: KnowledgeHubBrowseAllArticlesState) {
		if (!props.isLoading && !state.hasLoaded) {
			return {
				hasLoaded: true,
			};
		}
		return null;
	}

	state = {
		hasLoaded: false,
		spacesSelected: this.props.spaceKeys || {},
		query: this.props.searchQuery || '',
		dropdownText: this.triggerText(this.props.spaceKeys),
	};

	componentDidMount() {
		const { onFetchAppLink, getLinkedSpaces, projectKey } = this.props;
		onFetchAppLink();
		getLinkedSpaces({ projectKey });
	}

	componentDidUpdate(prevProps: InternalProps) {
		const { searchQuery, spaceKeys, page, sortBy, appLinkOrError } = this.props;
		if (
			page !== prevProps.page ||
			sortBy !== prevProps.sortBy ||
			searchQuery !== prevProps.searchQuery ||
			!isEqual(Object.keys(spaceKeys), Object.keys(prevProps.spaceKeys)) ||
			// @ts-expect-error - TS2339 - Property 'error' does not exist on type 'AppLink | AppLinkError'.
			(!prevProps.appLinkOrError?.error &&
				// @ts-expect-error - TS2339 - Property 'requireCredentials' does not exist on type 'AppLink | AppLinkError'.
				appLinkOrError?.requireCredentials === false &&
				// @ts-expect-error - TS2339 - Property 'requireCredentials' does not exist on type 'AppLink | AppLinkError'. | TS2339 - Property 'requireCredentials' does not exist on type 'AppLink | AppLinkError'.
				appLinkOrError?.requireCredentials !== prevProps.appLinkOrError?.requireCredentials)
		) {
			this.refreshArticles();
		}
	}

	refreshArticles() {
		const {
			cursor,
			searchQuery,
			spaceKeys,
			page,
			sortBy,
			onBrowseArticles,
			limit,
			createAnalyticsEvent,
		} = this.props;
		let browseObj = {
			id: 'all-articles',
			page,
			limit,
			expand: ['viewCount', 'category'],
			orderBy: sortBy,
			createAnalyticsEvent,
		};
		if (cursor) {
			// @ts-expect-error - TS2322 - Type '{ cursor: string; id: string; page: number; limit: number; expand: string[]; orderBy: OrderBy; }' is not assignable to type '{ id: string; page: number; limit: number; expand: string[]; orderBy: OrderBy; }'.
			browseObj = { ...browseObj, cursor };
		}

		if (searchQuery) {
			// @ts-expect-error - TS2322 - Type '{ query: string; id: string; page: number; limit: number; expand: string[]; orderBy: OrderBy; }' is not assignable to type '{ id: string; page: number; limit: number; expand: string[]; orderBy: OrderBy; }'.
			browseObj = { ...browseObj, query: searchQuery };
		}

		const spaces = Object.keys(spaceKeys);
		if (spaces.length) {
			// @ts-expect-error - TS2322 - Type '{ spaceKey: string[]; id: string; page: number; limit: number; expand: string[]; orderBy: OrderBy; }' is not assignable to type '{ id: string; page: number; limit: number; expand: string[]; orderBy: OrderBy; }'.
			browseObj = { ...browseObj, spaceKey: spaces };
		}

		// @ts-expect-error - TS2345 - Argument of type '{ id: string; page: number; limit: number; expand: string[]; orderBy: OrderBy; }' is not assignable to parameter of type 'BrowseKnowledgePayload'.
		onBrowseArticles(browseObj);
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	triggerText(spaces: Record<any, any>): string {
		const { intl } = this.props;
		const { length } = Object.keys(spaces);
		let spaceName = '';
		if (length === 1) {
			const key = Object.keys(spaces)[0];
			spaceName = spaces[key].length > 15 ? `${spaces[key].substr(0, 15)}...` : spaces[key];
		}
		return intl.formatMessage(messages.spacesDropdownText, {
			count: length,
			spaceName,
		});
	}

	getDropdownItemsV2(linkedSpaces: ConfluenceSpace[]): ReactNode[] {
		const { spacesSelected } = this.state;
		const dropdownItems = linkedSpaces.map((space, index) => (
			<DropdownItemCheckbox
				key={index}
				id={space.spaceKey}
				onClick={
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					this.onSpacesToggled as unknown as (
						e: React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element>,
					) => void
				}
				isSelected={space.spaceKey in spacesSelected}
			>
				{space.spaceName}
			</DropdownItemCheckbox>
		));
		return dropdownItems;
	}

	content(articleUrl: string | undefined, showSpaceColumn: boolean, canViewSpaceUrl: boolean) {
		const {
			articlesPage,
			page,
			sortBy,
			intl,
			limit,
			ArticlesTable,
			nextCursor,
			prevCursor,
			searchQuery,
			spaceKeys,
			categoriesSupported,
			appLinkOrError,
		} = this.props;
		let creatingLink = (
			<a href={articleUrl} target="_blank" rel="noreferrer noopener">
				{intl.formatMessage(messages.creatingArticleDescription)}
			</a>
		);

		const isConfluenceServerAppLink = Boolean(
			// @ts-expect-error - TS2339 - Property 'error' does not exist on type 'AppLink | AppLinkError'. | TS2339 - Property 'isServer' does not exist on type 'AppLink | AppLinkError'.
			!appLinkOrError?.error && appLinkOrError?.isServer,
		);
		if (!isConfluenceServerAppLink) {
			creatingLink = (
				<UserCapabilitiesSubscriber>
					{({ data }) => {
						const {
							hasConfluenceLicense,
							spaceUserCapabilities: { editableSpaces },
						} = data;
						return (
							<CreateArticleButton
								spaces={editableSpaces}
								appearance="link"
								hasConfluenceLicense={hasConfluenceLicense}
							>
								{intl.formatMessage(messages.creatingArticleDescription)}
							</CreateArticleButton>
						);
					}}
				</UserCapabilitiesSubscriber>
			);
		}

		const emptyStateComponentMultipleSpaces = (
			<EmptyStateContainer>
				<ImgContainer src={EmptyStateImage} alt="no article present" />
				<EmptyStateMiddleContainer>
					<Heading size="medium">{intl.formatMessage(messages.noArticlesTitle)}</Heading>
				</EmptyStateMiddleContainer>
				<CreateArticleLinkButton>
					<FormattedMessage
						{...messages.noArticlesDescriptionMultipleSpaces}
						values={{
							creating: creatingLink,
						}}
					/>
				</CreateArticleLinkButton>
			</EmptyStateContainer>
		);

		const noSearchResultsComponent = (
			<EmptyStateContainer>
				<ImgContainer src={searchNoResultsImage} alt="no article found" />
				<EmptyStateMiddleContainer>
					<Heading size="medium">
						{intl.formatMessage(messages.noArticlesTitleMultipleSpaces)}
					</Heading>
				</EmptyStateMiddleContainer>
				<CreateArticleLinkButton>
					<FormattedMessage
						{...messages.noArticlesDescriptionMultipleSpaces}
						values={{
							creating: creatingLink,
						}}
					/>
				</CreateArticleLinkButton>
			</EmptyStateContainer>
		);

		return (
			<ArticlesTable
				limit={limit}
				page={page}
				sortBy={sortBy}
				categoriesSupported={categoriesSupported}
				totalSize={articlesPage?.totalSize}
				knowledgePieces={articlesPage?.articles}
				emptyStateComponent={emptyStateComponentMultipleSpaces}
				noSearchResultsComponent={noSearchResultsComponent}
				nextCursor={nextCursor}
				prevCursor={prevCursor}
				searchQuery={searchQuery}
				spaceKeys={spaceKeys}
				showSpaceColumn={showSpaceColumn}
				canViewSpaceUrl={canViewSpaceUrl}
				appLinkOrError={appLinkOrError}
			/>
		);
	}

	onSetPage = (removeText: boolean) => {
		const { push, history, sortBy } = this.props;
		const { spacesSelected, query } = this.state;
		const spacesLength = Object.keys(spacesSelected).length;
		const spacekeyNames = isEmpty(spacesSelected) ? null : JSON.stringify(spacesSelected);

		let queryParams: Record<string, unknown> = { sortBy };
		if (removeText && spacesLength > 0) {
			queryParams = { ...queryParams, spaceKey: spacekeyNames };
		} else if (!removeText) {
			queryParams = {
				...queryParams,
				searchQuery: query,
				spaceKey: spacekeyNames,
			};
		}

		push
			? push(
					`/knowledge/browse/page/1?${qs.stringify(queryParams, {
						// @ts-expect-error - TS2345 - Argument of type '{ skipNull: boolean; skipEmptyString: boolean; }' is not assignable to parameter of type 'StringifyOptions'.
						skipNull: true,
						skipEmptyString: true,
					})}`,
				)
			: history.push(
					`/knowledge/browse/page/1?${qs.stringify(queryParams, {
						// @ts-expect-error - TS2345 - Argument of type '{ skipNull: boolean; skipEmptyString: boolean; }' is not assignable to parameter of type 'StringifyOptions'.
						skipNull: true,
						skipEmptyString: true,
					})}`,
				);
	};

	debouncedSearch = debounce(this.onSetPage, 300);

	onTextEntered = (e: SyntheticEvent<HTMLElement>) => {
		// @ts-expect-error - TS2339 - Property 'value' does not exist on type 'EventTarget & HTMLElement'.
		const newText = e.currentTarget.value;
		this.setState({
			query: newText != null ? newText : '',
		});
		this.debouncedSearch(false);
	};

	onRemoveText = () => {
		this.setState({ query: '' });
		this.debouncedSearch(true);
	};

	onSpacesToggled = (e: SyntheticEvent<HTMLElement>) => {
		const spaceKey = e.currentTarget.id;
		const spaceName = e.currentTarget.textContent;
		this.setState(
			(state) => {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				let spacesSelected: Record<string, any> = {};
				if (spaceKey in state.spacesSelected) {
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					spacesSelected = Object.keys(state.spacesSelected).reduce<Record<string, any>>(
						(acc, key) => {
							if (key !== spaceKey) {
								acc[key] = state.spacesSelected[key];
							}
							return acc;
						},
						{},
					);
					return {
						spacesSelected,
						dropdownText: this.triggerText(spacesSelected),
					};
				}
				spacesSelected = { ...state.spacesSelected, [spaceKey]: spaceName };
				return {
					spacesSelected,
					dropdownText: this.triggerText(spacesSelected),
				};
			},
			() => {
				const removeText = isEmpty(this.state.spacesSelected);
				this.onSetPage(removeText);
			},
		);
	};

	renderBottomBar() {
		const { intl, categoriesSupported, isAdmin, projectKey } = this.props;
		const settingsUrl = `/jira/servicedesk/projects/${projectKey}/settings/confluence-kb`;

		if (!categoriesSupported) {
			return <>{intl.formatMessage(messages.welcomeDescriptionNoCategories)}</>;
		}

		const CustomDescription = getWillShowNav4() ? DescriptionV2 : Description;
		return (
			<CustomDescription>
				<Inline alignBlock="start" spread="space-between" space="space.100">
					<Box>
						{getWillShowNav4() ? (
							<span>{intl.formatMessage(messages.welcomeDescriptionNew)} </span>
						) : (
							<p>{intl.formatMessage(messages.welcomeDescriptionNew)}</p>
						)}
						{isAdmin ? (
							<FormattedMessage
								{...messages.welcomeDescriptionAdmins}
								values={{
									linkText: (
										<a
											href={settingsUrl}
											// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
											css={css({
												textDecoration: 'underline',
											})}
										>
											{intl.formatMessage(messages.welcomeDescriptionAdminsLink)}
										</a>
									),
								}}
							/>
						) : null}
					</Box>
					{isAdmin && fg('jsm-va-show-touchpoint-in-kb') && <VirtualServiceAgentTouchpoint />}
				</Inline>
			</CustomDescription>
		);
	}

	renderAllArticlesView() {
		const {
			intl,
			confluenceUrl,
			spaceKey,
			onCreateArticleHeaderClicked,
			ReadyForUser,
			appLinkOrError,
			articlesPage,
		} = this.props;
		const { hasLoaded } = this.state;
		const articleUrl =
			confluenceUrl && spaceKey && `${confluenceUrl}/display/${spaceKey}?createDialog=true`;

		return (
			<KbContentContainer>
				{hasLoaded && articlesPage && articlesPage.totalSize >= 0 && (
					<ReadyForUser metricKey="servicedesk.knowledge.articles" metric={articlesPageLoad} />
				)}

				<ViewTracker />
				{hasLoaded && (
					<Box xcss={confluenceUpsellContainerStyles}>
						<ConfluenceUpsellMessage />
					</Box>
				)}
				{getWillShowNav4() ? (
					<>{this.renderBottomBar()}</>
				) : (
					<PageHeader
						// @ts-expect-error - TS2769 - No overload matches this call.
						truncateTitle
						bottomBar={this.renderBottomBar()}
						actions={
							<ActionsGroup spacing="wide">
								{renderLinkedButton({
									articleUrl,
									onCreateArticleHeaderClicked,
									intl,
									appLinkOrError,
								})}
							</ActionsGroup>
						}
					>
						{intl.formatMessage(commonMessages.articlesPageHeader)}
					</PageHeader>
				)}
				<AppLinkAuthentication onAuthComplete={this.onAuthComplete}>
					{this.renderAllArticleSearchAndTable(articleUrl)}
				</AppLinkAuthentication>
				<DraftArticleSuccessFlags />
			</KbContentContainer>
		);
	}

	renderSearchContainer(readableSpaces: ConfluenceSpace[], hasMultipleLinkedSpaces: boolean) {
		const { intl } = this.props;

		return (
			<SearchContainer>
				<TextFieldContainer>
					<Textfield
						elemAfterInput={
							this.state.query.length ? (
								<IconWrapper onClick={this.onRemoveText}>
									<SearchClose size="medium" label="close" />
								</IconWrapper>
							) : (
								<IconWrapper>
									<SearchIcon size="medium" label="" aria-hidden="true" />
								</IconWrapper>
							)
						}
						onChange={this.onTextEntered}
						placeholder={intl.formatMessage(messages.searchPlaceholder)}
						value={this.state.query}
						isCompact
						aria-label={intl.formatMessage(messages.searchLabelText)}
						aria-describedby="hintText"
					/>
					<span id="hintText" hidden>
						{intl.formatMessage(messages.searchArticlesHintText)}
					</span>
				</TextFieldContainer>
				<>
					{hasMultipleLinkedSpaces && (
						<DropdownContainer>
							<DropdownMenu
								// eslint-disable-next-line jira/react-no-inline-function-prop
								trigger={({ triggerRef, ...props }) => (
									<Button {...props} ref={triggerRef} iconAfter={<ChevronDownIcon label="" />}>
										{this.state.dropdownText}
									</Button>
								)}
							>
								<DropdownItemCheckboxGroup id="spaces-dropdown">
									{this.getDropdownItemsV2(readableSpaces)}
								</DropdownItemCheckboxGroup>
							</DropdownMenu>
						</DropdownContainer>
					)}
				</>
			</SearchContainer>
		);
	}

	renderAllArticleSearchAndTable(articleUrl?: string) {
		const { intl, onCreateArticleHeaderClicked, appLinkOrError } = this.props;
		return (
			<UserCapabilitiesSubscriber>
				{({ data }) => {
					const {
						hasConfluenceLicense,
						spaceUserCapabilities: { readableSpaces },
					} = data;
					const totalSize = readableSpaces.length;
					const hasMultipleLinkedSpaces = totalSize > 1;
					return (
						<>
							{getWillShowNav4() ? (
								<KnowledgeBaseActionsGroup>
									{this.renderSearchContainer(readableSpaces, hasMultipleLinkedSpaces)}
									{renderLinkedButton({
										articleUrl,
										onCreateArticleHeaderClicked,
										intl,
										appLinkOrError,
									})}
								</KnowledgeBaseActionsGroup>
							) : (
								this.renderSearchContainer(readableSpaces, hasMultipleLinkedSpaces)
							)}
							{this.content(articleUrl, hasMultipleLinkedSpaces, hasConfluenceLicense)}
						</>
					);
				}}
			</UserCapabilitiesSubscriber>
		);
	}

	renderAllArticlesViewWithAnalytics() {
		const { articlesPage, appLinkOrError } = this.props;

		if (articlesPage) {
			return (
				<AnalyticsContext
					data={{
						source: `knowledgeBrowseAllArticleSuccess${SCREEN}`,
						attributes: {
							isConfluenceServer: Boolean(
								// @ts-expect-error - TS2339 - Property 'error' does not exist on type 'AppLink | AppLinkError'. | TS2339 - Property 'isServer' does not exist on type 'AppLink | AppLinkError'.
								!appLinkOrError?.error && appLinkOrError?.isServer,
							),
						},
					}}
				>
					{this.renderAllArticlesView()}
				</AnalyticsContext>
			);
		}
		return this.renderAllArticlesView();
	}

	onAuthComplete = (error?: Error) => {
		if (!error) {
			this.refreshArticles();
			this.props.onFetchAppLink();
		}
	};

	render() {
		const { error, linkedState, appLinkOrError, projectId, createAnalyticsEvent, activationId } =
			this.props;
		if (error) {
			return (
				<ErrorPage
					error={error.error}
					screenName="knowledgeBrowseAllArticleError"
					attributes={{
						isConfluenceServer: Boolean(
							// @ts-expect-error - TS2339 - Property 'error' does not exist on type 'AppLink | AppLinkError'. | TS2339 - Property 'isServer' does not exist on type 'AppLink | AppLinkError'.
							!appLinkOrError?.error && appLinkOrError?.isServer,
						),
					}}
				/>
			);
		}

		if (linkedState === 'unlinked') {
			return <Redirect to="/knowledge/setup" />;
		}

		return (
			<UserCapabilitiesSubscriber>
				{(_, { fetchUserCapabilities }) => (
					<>
						<OpTrigger
							op={fetchUserCapabilities}
							args={[projectId, activationId, createAnalyticsEvent({})]}
						/>
						{this.renderAllArticlesViewWithAnalytics()}
					</>
				)}
			</UserCapabilitiesSubscriber>
		);
	}
}

export default injectIntl(KnowledgeHubBrowseAllArticles);

// eslint-disable-next-line @atlaskit/design-system/use-primitives, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DropdownContainer = styled.div({
	paddingTop: '0px',
	paddingRight: token('space.050', '4px'),
	paddingBottom: '0px',
	paddingLeft: token('space.050', '4px'),
	marginLeft: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TextFieldContainer = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${gridSize * 30}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 4}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	input: {
		paddingTop: token('space.050', '4px'),
		paddingRight: token('space.100', '8px'),
		paddingBottom: token('space.050', '4px'),
		paddingLeft: token('space.100', '8px'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${gridSize * 3}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 3}px`,
	display: 'flex',
	alignItems: 'center',
	cursor: 'pointer',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtle', colors.N200),
	marginRight: token('space.050', '4px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Description = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	maxWidth: `${gridSize * 96}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DescriptionV2 = styled.div({
	marginTop: token('space.200', '16px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EmptyStateContainer = styled.div({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	height: '62vh',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EmptyStateMiddleContainer = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text', colors.N800),
	marginTop: token('space.250', '20px'),
	marginRight: 0,
	marginBottom: token('space.100', '8px'),
	marginLeft: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ImgContainer = styled.img({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 20}px`,
	marginBottom: token('space.100'),
});

const confluenceUpsellContainerStyles = xcss({
	marginTop: 'space.300',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CreateArticleLinkButton = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> span': {
		display: 'flex',
		alignItems: 'center',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	button: {
		padding: '0',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SearchContainerOld = styled.div({
	display: 'flex',
	marginTop: token('space.300', '24px'),
	marginRight: 0,
	marginBottom: token('space.250', '20px'),
	marginLeft: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SearchContainerNew = styled.div({
	display: 'flex',
});

const SearchContainer = componentWithCondition(
	getWillShowNav4,
	SearchContainerNew,
	SearchContainerOld,
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const ActionsGroup = styled.div({
	display: 'flex',
	alignItems: 'center',
	/* stylelint-disable property-no-unknown */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	'> *': (props: { spacing: string }) => ({
		marginLeft: props.spacing === 'wide' ? token('space.200', '16px') : token('space.100', '8px'),
	}),
});
